import { t } from 'i18next';
import { getPhaseProgress, TPhaseSegment } from '../../../../../../shared/common/bossPhases';
import { safeParseInt } from '../../../../../../shared/common/utils';

export type THealthOverAttemptWidgetTypeSlug = 'attempt' | 'absoluteTime' | 'relativeTime';

export type THealthOverAttemptGuildParam = {
  guild: string;
  realm: string;
  region: string;
};

type THealthOverAttemptGuild = {
  displayName?: string | null;
  guild?: string | null;
  name?: string | null;
};

type TBuildHealthOverAttemptSubtitleOptions = {
  widgetTypeSubtitle?: string | null;
  guilds?: THealthOverAttemptGuild[] | null;
  guildsQueryParam?: unknown;
  difficultyLabel?: string | null;
  raidLabel?: string | null;
};

type TTranslateFn = (translationKey: string) => string;

export type THealthOverAttemptParsedConfig = {
  accessKey?: string;
  boss?: string;
  chromaRgb?: string;
  delay: number;
  difficulty?: string;
  embed: boolean;

  graphPhaseProgress: boolean;
  guilds: THealthOverAttemptGuildParam[];
  guildsQueryParam?: string;
  height: number | null;
  hideCredits: boolean;
  hideData: string[];
  hideDays: boolean;
  hideLogo: boolean;
  hidePoweredBy: boolean;
  maxPull: number | null;
  minPull: number | null;
  period?: string;
  raid?: string;
  refreshMs: number;
  seriesColors?: string;
  showDottedLines: boolean;
  skipAfter?: string;
  skipBefore?: string;
  skipFirst: number;
  test: boolean;
  theme?: string;
  type: THealthOverAttemptWidgetTypeSlug;
  phases: string[];
  phasesParam?: string;
  zoomPhases: string[];
  zoomPhasesParam?: string;
};

type THealthOverAttemptSeriesPoint = {
  custom?: {
    durationMs?: number | null;
  };
  [key: string]: any;
};

type THealthOverAttemptSeries = {
  data: THealthOverAttemptSeriesPoint[];
  [key: string]: any;
};

function clamp(value: number, minValue: number, maxValue: number) {
  return Math.min(maxValue, Math.max(minValue, value));
}

export function doesPhaseSegmentContainMetricValue(segment: TPhaseSegment, metricValue: number) {
  if (segment.minExclusive ? metricValue <= segment.minValue : metricValue < segment.minValue) {
    return false;
  }

  if (typeof segment.maxValue === 'number') {
    if (segment.maxExclusive ? metricValue >= segment.maxValue : metricValue > segment.maxValue) {
      return false;
    }
  }

  return true;
}

export function filterSeriesDataByTimePhaseSegments(
  seriesData: THealthOverAttemptSeries[],
  phasesSegments: TPhaseSegment[],
  currentShownPhaseIndexes: [number, number]
) {
  return seriesData.map((series) => {
    const filteredData = series.data.filter((point) => {
      const durationMs = point?.custom?.durationMs;
      if (typeof durationMs !== 'number') {
        return false;
      }

      const overallPercent = typeof point?.y === 'number' ? point.y / 100 : null;
      const matchingSegmentIndex = phasesSegments.reduce((matchedIndex, segment, index) => {
        const metricValue = segment.metric === 'elapsedMs' ? durationMs : overallPercent;
        if (typeof metricValue !== 'number' || !doesPhaseSegmentContainMetricValue(segment, metricValue)) {
          return matchedIndex;
        }

        return index;
      }, -1);

      return matchingSegmentIndex >= currentShownPhaseIndexes[0] && matchingSegmentIndex <= currentShownPhaseIndexes[1];
    });

    return {
      ...series,
      data: filteredData
    };
  });
}

export function filterSeriesDataByPhaseSegmentIndexes(
  seriesData: THealthOverAttemptSeries[],
  difficulty: string,
  bossSlug: string,
  phasesSegments: TPhaseSegment[],
  currentShownPhaseIndexes: [number, number]
) {
  return seriesData.map((series) => {
    const filteredData = series.data.filter((point) => {
      if (typeof point?.y !== 'number') {
        return false;
      }

      const phaseProgress = getPhaseProgress(difficulty, bossSlug, {
        overallPercent: point.y / 100,
        elapsedMs: point?.custom?.durationMs
      });
      if (!phaseProgress) {
        return false;
      }

      const matchingSegmentIndex = phasesSegments.findIndex((segment) => segment.phase === phaseProgress.phase);
      return matchingSegmentIndex >= currentShownPhaseIndexes[0] && matchingSegmentIndex <= currentShownPhaseIndexes[1];
    });

    return {
      ...series,
      data: filteredData
    };
  });
}

export function buildPercentPhaseAxisConfig(
  phasesSegments: TPhaseSegment[],
  currentShownPercentages: [number, number]
) {
  const [minPercent, maxPercent] = currentShownPercentages;
  const tickPositions = [
    minPercent * 100,
    ...phasesSegments
      .filter((segment) => typeof segment.maxValue === 'number')
      .map((segment) => (segment.maxValue as number) * 100),
    maxPercent * 100
  ]
    .sort((leftValue, rightValue) => leftValue - rightValue)
    .filter((value, index, values) => index === 0 || Math.abs(value - values[index - 1]) > Number.EPSILON);

  return {
    min: minPercent * 100,
    max: maxPercent * 100,
    tickPositions
  };
}

function getFirstQueryParamValue(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    for (const innerValue of value) {
      const parsedValue = getFirstQueryParamValue(innerValue);
      if (typeof parsedValue !== 'undefined') {
        return parsedValue;
      }
    }

    return undefined;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    return trimmedValue ? trimmedValue : undefined;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return undefined;
}

function parseBooleanQueryParam(value: unknown, defaultValue: boolean) {
  if (typeof value === 'undefined' || value === null || value === '') {
    return defaultValue;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    return parseBooleanQueryParam(value[0], defaultValue);
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();

    if (['0', 'false', 'no', 'off'].includes(normalizedValue)) {
      return false;
    }

    if (['1', 'true', 'yes', 'on'].includes(normalizedValue)) {
      return true;
    }
  }

  return Boolean(value);
}

function parsePositiveIntegerQueryParam(value: unknown) {
  const parsedValue = safeParseInt(getFirstQueryParamValue(value) || '', 0);
  return parsedValue > 0 ? parsedValue : null;
}

function parseNonNegativeIntegerQueryParam(value: unknown, defaultValue = 0) {
  const normalizedValue = getFirstQueryParamValue(value);

  if (typeof normalizedValue === 'undefined') {
    return defaultValue;
  }

  const parsedValue = safeParseInt(normalizedValue, defaultValue);
  return parsedValue >= 0 ? parsedValue : defaultValue;
}

function parseCsvQueryParam(value: unknown) {
  const normalizedValue = getFirstQueryParamValue(value);

  if (!normalizedValue) {
    return [];
  }

  return normalizedValue
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function getAliasedQueryParamValue(queryParams: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = getFirstQueryParamValue(queryParams[key]);

    if (typeof value !== 'undefined') {
      return value;
    }
  }

  return undefined;
}

function normalizeSubtitleValue(value: unknown) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return undefined;
  }

  const normalizedValue = trimmedValue.toLowerCase();
  if (normalizedValue === 'undefined' || normalizedValue === 'null') {
    return undefined;
  }

  return trimmedValue;
}

function buildGuildSubtitleLabel(guildNames: string[]) {
  if (guildNames.length === 0) {
    return undefined;
  }

  if (guildNames.length <= 3) {
    return guildNames.join(' / ');
  }

  return `${guildNames.slice(0, 3).join(' / ')} +${guildNames.length - 3}`;
}

function getGuildNamesFromData(guilds?: THealthOverAttemptGuild[] | null) {
  return (guilds || [])
    .map((guild) => normalizeSubtitleValue(guild.displayName || guild.name || guild.guild))
    .filter((guildName): guildName is string => Boolean(guildName));
}

function getGuildNamesFromQueryParam(guildsQueryParam?: unknown) {
  return parseHealthOverAttemptGuildParams(guildsQueryParam)
    .map((guildInfo) => normalizeSubtitleValue(guildInfo.guild))
    .filter((guildName): guildName is string => Boolean(guildName));
}

export function parseHealthOverAttemptGuildParams(guildsQueryParam?: unknown): THealthOverAttemptGuildParam[] {
  if (!guildsQueryParam) {
    return [];
  }

  if (Array.isArray(guildsQueryParam)) {
    return guildsQueryParam.reduce<THealthOverAttemptGuildParam[]>((parsedGuilds, value) => {
      parsedGuilds.push(...parseHealthOverAttemptGuildParams(value));
      return parsedGuilds;
    }, []);
  }

  if (typeof guildsQueryParam !== 'string') {
    return [];
  }

  const trimmedValue = guildsQueryParam.trim();
  if (!trimmedValue) {
    return [];
  }

  const separator = trimmedValue.includes('|') ? '|' : ',';

  return trimmedValue
    .split(separator)
    .map((value) => value.trim())
    .filter(Boolean)
    .map((line) => {
      const [region, realm, ...guildParts] = line.split('/');
      const guild = guildParts.join('/').trim();

      if (!region || !realm || !guild) {
        return null;
      }

      return {
        guild,
        realm: realm.trim(),
        region: region.trim()
      };
    })
    .filter((guildInfo): guildInfo is THealthOverAttemptGuildParam => Boolean(guildInfo));
}

export function getTranslatedHealthOverAttemptLabel(
  translationKey?: string,
  fallbackValue?: string | null,
  translate: TTranslateFn = t
) {
  const normalizedFallbackValue =
    typeof fallbackValue === 'string' && fallbackValue.trim() ? fallbackValue.trim() : undefined;

  if (!translationKey) {
    return normalizedFallbackValue;
  }

  const translatedValue = translate(translationKey);
  const normalizedTranslatedValue =
    typeof translatedValue === 'string' && translatedValue.trim() ? translatedValue.trim() : undefined;

  if (
    !normalizedTranslatedValue ||
    normalizedTranslatedValue === translationKey ||
    normalizedTranslatedValue.toLowerCase() === 'undefined' ||
    normalizedTranslatedValue.toLowerCase() === 'null'
  ) {
    return normalizedFallbackValue;
  }

  return normalizedTranslatedValue;
}

export function buildHealthOverAttemptSubtitle(options: TBuildHealthOverAttemptSubtitleOptions) {
  const guildNames = getGuildNamesFromData(options.guilds);
  const fallbackGuildNames = guildNames.length > 0 ? guildNames : getGuildNamesFromQueryParam(options.guildsQueryParam);
  const guildLabel = buildGuildSubtitleLabel(fallbackGuildNames);
  const primaryLabel = guildLabel || normalizeSubtitleValue(options.widgetTypeSubtitle);
  const encounterLabel = [normalizeSubtitleValue(options.difficultyLabel), normalizeSubtitleValue(options.raidLabel)]
    .filter((label): label is string => Boolean(label))
    .join(' ');

  return [primaryLabel, encounterLabel].filter((label): label is string => Boolean(label)).join(' • ');
}

export function buildHealthOverAttemptBackgroundColor(chromaRgb?: string) {
  const normalizedValue = getFirstQueryParamValue(chromaRgb);

  if (!normalizedValue) {
    return '';
  }

  if (normalizedValue === 'transparent') {
    return 'rgba(0,0,0,0)';
  }

  const sanitizedValue = normalizedValue.replace(/^#/, '').toLowerCase();
  const expandedValue =
    sanitizedValue.length === 3
      ? sanitizedValue
          .split('')
          .map((character) => `${character}${character}`)
          .join('')
      : sanitizedValue;

  if (!/^[0-9a-f]{6}$/.test(expandedValue)) {
    return '';
  }

  return `rgba(${parseInt(expandedValue.slice(0, 2), 16)},${parseInt(expandedValue.slice(2, 4), 16)},${parseInt(
    expandedValue.slice(4, 6),
    16
  )},1)`;
}

export function parseHealthOverAttemptConfig(queryParams: Record<string, unknown>) {
  const guildsQueryParam = getFirstQueryParamValue(queryParams.guilds);
  const refreshSeconds = clamp(parseNonNegativeIntegerQueryParam(queryParams.refresh, 60), 0, 3600);
  const typeValue = getFirstQueryParamValue(queryParams.type);
  const type: THealthOverAttemptWidgetTypeSlug =
    typeValue === 'absoluteTime' || typeValue === 'relativeTime' ? typeValue : 'attempt';
  const phasesParam = getAliasedQueryParamValue(queryParams, 'phases');
  const zoomPhasesParam = getAliasedQueryParamValue(queryParams, 'zoom_phases', 'zoomPhases', 'zoomPhasesParam');

  return {
    accessKey: getAliasedQueryParamValue(queryParams, 'access_key', 'accessKey'),
    boss: getFirstQueryParamValue(queryParams.boss),
    chromaRgb: getFirstQueryParamValue(queryParams.chromargb),
    delay: parseNonNegativeIntegerQueryParam(queryParams.delay, 0),
    difficulty: getFirstQueryParamValue(queryParams.difficulty),
    embed: parseBooleanQueryParam(queryParams.embed, false),

    graphPhaseProgress: parseBooleanQueryParam(queryParams.graphPhaseProgress, true),
    guilds: parseHealthOverAttemptGuildParams(guildsQueryParam),
    guildsQueryParam,
    height: parsePositiveIntegerQueryParam(queryParams.height),
    hideCredits: parseBooleanQueryParam(queryParams.hideCredits, false),
    hideData: parseCsvQueryParam(queryParams.hide),
    hideDays: parseBooleanQueryParam(queryParams.hideDays, false),
    hideLogo: parseBooleanQueryParam(getAliasedQueryParamValue(queryParams, 'hide_logo', 'hideLogo'), false),
    hidePoweredBy: parseBooleanQueryParam(
      getAliasedQueryParamValue(queryParams, 'hide_poweredby', 'hidePoweredBy'),
      false
    ),
    maxPull: parsePositiveIntegerQueryParam(getAliasedQueryParamValue(queryParams, 'max_pull', 'maxPull')),
    minPull: parsePositiveIntegerQueryParam(getAliasedQueryParamValue(queryParams, 'min_pull', 'minPull')),
    period: getFirstQueryParamValue(queryParams.period),
    raid: getFirstQueryParamValue(queryParams.raid),
    refreshMs: refreshSeconds * 1000,
    seriesColors: getAliasedQueryParamValue(queryParams, 'series_colors', 'seriesColors'),
    showDottedLines: parseBooleanQueryParam(
      getAliasedQueryParamValue(queryParams, 'show_dotted_lines', 'showDottedLines'),
      true
    ),
    skipAfter: getFirstQueryParamValue(queryParams.skipAfter),
    skipBefore: getFirstQueryParamValue(queryParams.skipBefore),
    skipFirst: parseNonNegativeIntegerQueryParam(queryParams.skipFirst, 0),
    test: parseBooleanQueryParam(queryParams.test, false),
    theme: getFirstQueryParamValue(queryParams.theme),
    type,
    phases: phasesParam
      ? phasesParam
          .split(',')
          .map((phase) => phase.trim())
          .filter(Boolean)
      : [],
    phasesParam,
    zoomPhases: zoomPhasesParam
      ? zoomPhasesParam
          .split(',')
          .map((phase) => phase.trim())
          .filter(Boolean)
      : [],
    zoomPhasesParam
  };
}
